import {
  FieldType,
  ViewDefinitionTransformer,
} from 'components/molecules/views';
import {
  asColumnOrder,
  ColumnOrder,
  withValidColumnOrderProps,
} from 'components/organisms/grid-custom-column';
import { Item as KanbanBoardItem } from 'components/organisms/kanban-board/group-by';
import { FieldFilter } from 'components/organisms/tasks/filters';
import {
  from_grouped_filter,
  to_grouped_filter,
} from 'components/pages/api-grouped-filter-transformer';
import { GroupFieldsMeta } from 'components/pages/filter-transformer';
import { FilterGroupType } from 'components/pages/types';
import {
  defaultKanbanBoardColumnGroup,
  defaultKanbanBoardRowGroup,
} from '../constants';
import { defaultGroupedFilter } from '../utils';
import { fromGroup, toGroup } from './transformer';
import { ApiTasksViewDefinition, GroupedFilterViewDefinition } from './types';

export const getGroupedViewDefinitionTransformer = (
  lookup: GroupFieldsMeta,
  defaultGroup: FilterGroupType,
  kanbanBoardRowItems: KanbanBoardItem[],
  kanbanBoardColumnItems: KanbanBoardItem[],
  defaultColumnOrder: ColumnOrder[],
): ViewDefinitionTransformer<
  ApiTasksViewDefinition,
  GroupedFilterViewDefinition
> => ({
  from: ({
    filter: viewDefinitionFilter,
    group,
    columnOrder,
  }: ApiTasksViewDefinition): GroupedFilterViewDefinition => {
    return {
      filter: viewDefinitionFilter
        ? from_grouped_filter(lookup, defaultGroup)(viewDefinitionFilter)
        : defaultGroupedFilter(FilterGroupType.Task),
      groupBy: {
        rowGroup: fromGroup({
          fieldsList: kanbanBoardRowItems,
          fieldGroup: group?.row || null,
          defaultValue: defaultKanbanBoardRowGroup,
        }),
        columnGroup: fromGroup({
          fieldsList: kanbanBoardColumnItems,
          fieldGroup: group?.column,
          defaultValue: defaultKanbanBoardColumnGroup,
        }),
      },
      columnOrder: columnOrder?.map(withValidColumnOrderProps),
    };
  },
  to: ({
    filter: viewDefinitionFilter,
    groupBy,
    columnOrder,
  }: GroupedFilterViewDefinition): ApiTasksViewDefinition => {
    const isFilterValid =
      viewDefinitionFilter.groupCriteria.filter((x) =>
        FieldFilter.isValid(x.criteria),
      ).length > 0;

    return {
      filter: isFilterValid
        ? to_grouped_filter(viewDefinitionFilter)
        : undefined,
      group: {
        row: toGroup({
          fieldsList: kanbanBoardRowItems,
          fieldName: groupBy?.rowGroup,
        }),
        column: toGroup({
          fieldsList: kanbanBoardColumnItems,
          fieldName: groupBy?.columnGroup,
        }) ?? {
          name: defaultKanbanBoardColumnGroup,
          fieldClass: FieldType.Native,
        },
      },
      columnOrder:
        JSON.stringify(defaultColumnOrder) === JSON.stringify(columnOrder)
          ? undefined
          : asColumnOrder(columnOrder),
    };
  },
});
